import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FilterSelect } from "@/components/ui/filter-select";
import { FilterMultiSelect } from "@/components/ui/filter-multi-select";
import { StoreSelectorValue } from "@/@types/campaign";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import {
  Store,
  AlertCircle,
  Loader2,
  RefreshCw,
  Check,
  Search,
  CheckCheck,
  Square,
  X,
  Upload,
  Download,
  Trash2,
} from "lucide-react";
import { useGetStoresFilterableOptionsQuery } from "@/services/store/select-options";
import { useStoreSelectionFilter } from "./use-store-selection-filter";
import {
  buildStoreTemplate,
  useStoreExcelImport,
} from "./use-store-excel-import";
import { StoreExcelUploadPreview } from "./store-excel-upload-preview";

interface StoreSelectorProps {
  allStores: boolean;
  selectedStores: number[];
  updateData: (data: Partial<StoreSelectorValue>) => void;
  error?: string;
  clearError?: () => void;
  hideAllStoresOption?: boolean;
}

export function StoreSelector({
  allStores,
  selectedStores,
  updateData,
  error,
  clearError,
  hideAllStoresOption = false,
}: StoreSelectorProps) {
  const { data: storesData, isLoading, isError, refetch } =
    useGetStoresFilterableOptionsQuery();

  const {
    normalizedStores,
    stateOptions,
    regionalOptions,
    filteredStores,
    selectedStates,
    toggleState,
    setSelectedStates,
    selectedRegional,
    setSelectedRegional,
    searchTerm,
    setSearchTerm,
    toggleAllFiltered,
    allFilteredSelected,
    clearFilters,
    hasActiveFilters,
  } = useStoreSelectionFilter(storesData || [], selectedStores);

  const toggleStore = (storeCode: number) => {
    const next = selectedStores.includes(storeCode)
      ? selectedStores.filter(code => code !== storeCode)
      : [...selectedStores, storeCode];
    updateData({ stores: next });
    clearError?.();
  };

  const handleAllStoresChange = (isAll: boolean) => {
    updateData({ allStores: isAll, stores: isAll ? [] : selectedStores });
    clearError?.();
  };

  const handleToggleAllFiltered = () => {
    updateData({ stores: toggleAllFiltered() });
    clearError?.();
  };

  const handleClearSelection = () => {
    updateData({ stores: [] });
    clearError?.();
  };

  const {
    fileInputRef,
    isProcessing,
    preview,
    handleFileUpload,
    closePreview,
    confirmImport,
  } = useStoreExcelImport(normalizedStores, selectedStores);

  const handleConfirmImport = (matchedCodes: number[]) => {
    confirmImport(matchedCodes, codes => {
      updateData({ stores: codes });
      clearError?.();
    });
    const found = preview?.matchedStores.length ?? matchedCodes.length;
    const notFound = preview?.notFound.length ?? 0;
    toast({
      title: "Importação concluída",
      description: `${found} loja(s) selecionada(s) via Excel${
        notFound > 0 ? `, ${notFound} não encontrada(s)` : ""
      }.`,
      variant: "default",
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5 text-primary" />
            <div>
              <h3 className="text-base font-semibold">Lojas Participantes *</h3>
              <p className="text-sm text-muted-foreground">
                Selecione quais lojas participarão desta campanha
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>

        {!hideAllStoresOption && (
        <RadioGroup
          value={allStores ? "all" : "specific"}
          onValueChange={value => handleAllStoresChange(value === "all")}
        >
          <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
            <RadioGroupItem value="all" id="all-stores" />
            <Label htmlFor="all-stores" className="font-normal cursor-pointer flex-1">
              <div>
                <p className="font-medium">Todas as lojas</p>
                <p className="text-sm text-gray-600">
                  Código 999 - A campanha será aplicada em todas as lojas
                </p>
              </div>
            </Label>
          </div>
          <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-secondary/50 transition-colors cursor-pointer">
            <RadioGroupItem value="specific" id="specific-stores" />
            <Label htmlFor="specific-stores" className="font-normal cursor-pointer flex-1">
              <div>
                <p className="font-medium">Lojas específicas</p>
                <p className="text-sm text-gray-600">
                  Selecione manualmente quais lojas participarão
                </p>
              </div>
            </Label>
          </div>
        </RadioGroup>
        )}

        {!allStores && (
          <div className="mt-4 space-y-2 p-4 border rounded-md bg-gray-50">
            <p className="text-sm font-medium mb-3">Selecione as lojas:</p>

            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="ml-2 text-sm text-gray-600">Carregando lojas...</span>
              </div>
            )}

            {isError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-sm text-red-800 font-medium">Erro ao carregar lojas</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="flex items-center gap-2 mx-auto border-red-300 text-red-700 hover:bg-red-100"
                >
                  <RefreshCw className="h-4 w-4" />
                  Tentar novamente
                </Button>
              </div>
            )}

            {!isLoading && !isError && normalizedStores.length > 0 && (
              <>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <FilterMultiSelect
                    label="Estado"
                    placeholder="Estado"
                    options={stateOptions}
                    values={selectedStates}
                    onToggle={toggleState}
                    onClear={() => setSelectedStates([])}
                    popoverWidth="w-[180px]"
                  />
                  <FilterSelect
                    label="Regional"
                    placeholder="Regional"
                    options={regionalOptions}
                    value={selectedRegional}
                    onChange={setSelectedRegional}
                    popoverWidth="w-[220px]"
                  />
                  {hasActiveFilters && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="h-8 px-2 text-muted-foreground"
                    >
                      <X className="mr-1 h-3 w-3" />
                      Limpar filtros
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={buildStoreTemplate}
                    className="h-8 flex items-center gap-1"
                  >
                    <Download className="h-4 w-4" />
                    Baixar modelo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessing}
                    className="h-8 flex items-center gap-1"
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Importar Excel
                  </Button>
                  {selectedStores.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClearSelection}
                      className="h-8 flex items-center gap-1 text-muted-foreground"
                    >
                      <Trash2 className="h-4 w-4" />
                      Limpar seleção
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar loja por nome ou código..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>

                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground">
                    {filteredStores.length} loja(s) no filtro
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleToggleAllFiltered}
                    disabled={filteredStores.length === 0}
                    className="h-8 flex items-center gap-1"
                  >
                    {allFilteredSelected ? (
                      <>
                        <Square className="h-4 w-4" />
                        Desmarcar todas
                      </>
                    ) : (
                      <>
                        <CheckCheck className="h-4 w-4" />
                        Selecionar todas
                      </>
                    )}
                  </Button>
                </div>

                <ScrollArea className="h-[280px] rounded-md border">
                  <div className="p-2">
                    {filteredStores.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {filteredStores.map(store => (
                          <div
                            key={store.code}
                            className="flex items-center space-x-2 p-2 hover:bg-white rounded transition-colors"
                          >
                            <Checkbox
                              id={`store-${store.code}`}
                              checked={selectedStores.includes(store.code)}
                              onCheckedChange={() => toggleStore(store.code)}
                            />
                            <Label
                              htmlFor={`store-${store.code}`}
                              className="font-normal cursor-pointer flex-1"
                            >
                              {store.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                        <Search className="h-8 w-8 mb-2 opacity-50" />
                        <p className="text-sm">Nenhuma loja encontrada</p>
                        <p className="text-xs text-muted-foreground">
                          Ajuste os filtros ou o termo de busca
                        </p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </>
            )}

            {!isLoading && !isError && normalizedStores.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Store className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm mb-3">Nenhuma loja disponível</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="flex items-center gap-2 mx-auto"
                >
                  <RefreshCw className="h-4 w-4" />
                  Procurar de novo
                </Button>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {error}
              </p>
            )}

            {selectedStores.length > 0 && (
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <p className="text-sm text-green-600 mt-2 font-medium flex items-center gap-1 cursor-help underline decoration-dotted">
                      <Check className="h-4 w-4" /> {selectedStores.length} loja(s)
                      selecionada(s)
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-xs">
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-xs text-muted-foreground uppercase mb-2">
                        Lojas Selecionadas
                      </p>
                      <ScrollArea className="max-h-[200px]">
                        {normalizedStores
                          .filter(store => selectedStores.includes(store.code))
                          .slice(0, 10)
                          .map(store => (
                            <div
                              key={store.code}
                              className="flex items-center gap-2 py-0.5"
                            >
                              <span className="font-mono text-xs bg-muted px-1 rounded">
                                {store.code}
                              </span>
                              <span className="truncate">{store.name}</span>
                            </div>
                          ))}
                        {selectedStores.length > 10 && (
                          <div className="text-muted-foreground italic text-xs pt-1 border-t mt-1">
                            ... e mais {selectedStores.length - 10} loja(s)
                          </div>
                        )}
                      </ScrollArea>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}

        {!hideAllStoresOption && (
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-3">
          <div className="flex gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                Multi-Loja
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-400">
                {allStores
                  ? "Será criado 1 registro com código 999 (todas as lojas)"
                  : selectedStores.length > 0
                  ? `Serão criados ${selectedStores.length} registros, um para cada loja selecionada, todos com o mesmo código de campanha`
                  : "Selecione as lojas para ver quantos registros serão criados"}
              </p>
            </div>
          </div>
        </div>
        )}
      </div>

      {preview && (
        <StoreExcelUploadPreview
          isOpen={!!preview}
          onClose={closePreview}
          onConfirm={handleConfirmImport}
          matchedStores={preview.matchedStores}
          alreadySelected={preview.alreadySelected}
          notFound={preview.notFound}
        />
      )}
    </div>
  );
}
